// Cardholder portal footer links (CAMS wrapper + pre-login pages).
//
// The white label API serves these as an ordered list, so display order is fixed centrally
// and must not be re-derived here. Each entry carries both languages:
//   { key, title_en, title_fr, url_en, url_fr }
//
// Separate from the balance checker's footer links, which come from theme.brand.cbc_footer
// as an unordered object with a single URL per link.

const blank = value => !String(value || '').trim();

// Prefer the viewing language, fall back to the other one. Clients with a single bilingual
// destination fill in the English field only, so French viewers must still get a live link.
const pick = (preferred, fallback) => (blank(preferred) ? String(fallback || '').trim() : String(preferred).trim());

// A link that points at another site (http://, https://, or protocol-relative //) leaves the
// portal, so it opens in a new tab and the cardholder keeps their place. Anything else is an
// in-portal path and navigates in the same tab. mailto:/tel: stay in the same tab too --
// they hand off to another app rather than opening a page.
export const isExternalUrl = url => /^(https?:)?\/\//i.test(String(url || '').trim());

/**
 * Resolve the ordered footer link list for the language being viewed.
 *
 * A link is dropped when it has no URL, or no title, in either language -- rendering it
 * would produce a dead or unlabelled link.
 *
 * @param {Array} cardholderFooter ordered list from theme.brand.cardholder_footer
 * @param {string} locale active UI locale ('en' | 'fr')
 * @returns {Array<{key: string, title: string, url: string, external: boolean}>}
 */
export const resolveCardholderFooterLinks = (cardholderFooter, locale) => {
  if (!Array.isArray(cardholderFooter)) return [];

  const isFr = locale === 'fr';

  return cardholderFooter
    .map(link => ({
      key: link?.key || link?.url_en || link?.url_fr,
      title: isFr
        ? pick(link?.title_fr, link?.title_en)
        : pick(link?.title_en, link?.title_fr),
      url: isFr ? pick(link?.url_fr, link?.url_en) : pick(link?.url_en, link?.url_fr),
    }))
    .filter(link => link.url && link.title)
    .map(link => ({ ...link, external: isExternalUrl(link.url) }));
};

export default resolveCardholderFooterLinks;
